import * as React from 'react';
import { filter, map, noop } from 'es-toolkit/compat';

import { ExpandableParagraph, Typography } from 'omakase';
import { Grid } from 'ui';
import { core } from '@tock/coretypes';
import {
  generateOptionGroupOptionProps,
  generateOptionGroupProps,
} from '@tock/shared/utils/option';
import { usePriceDisplayStyle } from 'utils/priceDisplayUtils';
import MenuItem from 'components/business/menu/MenuItem';

interface Props {
  ticketType: core.ITicketType;
  ticketOptionGroup: core.ITicketOptionGroup;
  optionsWithModifiers: number[];
  modifierOptionGroups: core.ITicketOptionGroup[];
  ticketOptions: core.ITicketOption[];
  ticketOptionPrices: core.IPrice[];
  patron?: core.IPatron;
}

const MenuGroup: React.FunctionComponent<Props> = ({
  ticketType: { optionExclusion, variety },
  ticketOptionGroup,
  ticketOptions,
  ticketOptionPrices,
  optionsWithModifiers,
  modifierOptionGroups,
  patron,
}) => {
  const useInclusivePricing = usePriceDisplayStyle(variety ?? undefined) === 'MIN_REQUIRED_SPEND';

  const { sortedTicketOptions, numSelectedInGroup } = generateOptionGroupProps({
    optionOrders: [],
    ticketOptions,
    onChangeQuantity: noop,
    patron,
    ticketOptionGroup,
  });

  const supplements = map(sortedTicketOptions, (ticketOption) => {
    const { price } = generateOptionGroupOptionProps({
      ticketOption,
      ticketOptionGroup,
      ticketOptions,
      partySize: 0,
      numSelectedInGroup,
      ticketOptionPrices,
      optionOrders: [],
      optionExclusion: optionExclusion ?? [],
      useInclusivePricing,
    });

    return (
      <MenuItem
        description={ticketOption.description ?? ''}
        modifiers={filter(
          modifierOptionGroups,
          (modifierGroup) => modifierGroup.parentTicketOptionId === ticketOption.id
        )}
        key={ticketOption.id as number}
        name={ticketOption.name ?? ''}
        image={ticketOption.image ?? undefined}
        price={price}
        ticketTypeVariety={variety ?? undefined}
      />
    );
  });

  return (
    <>
      <Typography component="h2" variant="body01Strong" mb={0.5}>
        {ticketOptionGroup.name}
      </Typography>
      {ticketOptionGroup.description && (
        <ExpandableParagraph
          paragraphText={ticketOptionGroup.description}
          linkify={true}
          cutoffLength={255}
          mb={1.5}
        />
      )}
      <Grid container spacing={1} data-testid="supplementGroup-supplements">
        {supplements}
      </Grid>
    </>
  );
};

export default MenuGroup;
